import { useCallback, useEffect, useRef, useState } from 'react';
import { useSafeTimeout } from './useSafeTimeout';
import { useRetainedRef, type RetainedToken } from './retainedState';

// content-size 변화가 이 시간 동안 없으면 "안정"으로 보고 복원을 확정한다.
const STABLE_MS = 200;

export type ScrollRestoration = {
  /** 스크롤 정지 시점(onMomentumEnd/onEndDrag)에 현재 offset 저장. 워클릿에서 scheduleOnRN 으로 호출 가능. */
  save: (offset: number) => void;
  /** 리스트의 onContentSizeChange 에 연결. 콘텐츠가 자라 target 에 도달하면 1회 복원. */
  onContentSizeChange: (width: number, contentHeight: number) => void;
  /** 복원 위치가 잡힐 때까지 true — 리스트를 숨겨(opacity:0) "top 보였다 점프" 깜빡임 제거. */
  restoring: boolean;
};

/**
 * 동적 높이 리스트의 스크롤 위치를 visit(retainedState) 단위로 저장/복원한다.
 * 가려진 화면 unmount→remount(같은 visit)를 가로질러 위치를 보존한다.
 *
 * - 저장: save(offset) — 정지 시점에만(매 프레임 X)
 * - 복원: onContentSizeChange 로 콘텐츠가 target 까지 자라면 scrollTo 1회. 못 닿거나 로딩이
 *   늦으면 maxWaitMs 후 도달 최대치로 클램프하며 노출(무한 블랭크 방지).
 * - restoring 동안 리스트를 숨겨 깜빡임 제거. 새 visit(저장값 0)은 복원/숨김 하지 않음.
 *
 * token 은 호출부 모듈 스코프에 `retainedState(0)` 로 선언해 넘긴다.
 */
export const useScrollRestoration = (params: {
  token: RetainedToken<number>;
  /** offset 으로 스크롤(보통 listRef.scrollToOffset 래핑). 복원은 animated=false 로 호출된다. */
  scrollTo: (offset: number, animated: boolean) => void;
  /** 리스트 뷰포트 높이 — contentHeight 가 target+viewport 이상이어야 target 도달 가능. */
  viewportHeight: number;
  maxWaitMs?: number;
}): ScrollRestoration => {
  const { token, scrollTo, viewportHeight, maxWaitMs = 2000 } = params;
  const setSafeTimeout = useSafeTimeout();
  const savedOffset = useRetainedRef(token);

  const restoredRef = useRef(false);
  // mount 시점 스냅샷(per-mount): 이번 mount 가 복원할 목표. 이후 save 는 다음 visit 용.
  const targetRef = useRef(savedOffset.current);
  const [restoring, setRestoring] = useState(targetRef.current > 0);

  // 리렌더 0 — visit 동안 동일 retained 슬롯에 기록.
  // 단, 복원이 끝나기 전(restoring)엔 저장하지 않는다. 복원 중 콘텐츠 높이 요동으로
  // 스크롤이 클램프되며 튀는 값(예: 7378)이 저장돼 다음 복원 target 을 오염시키던 문제 방지.
  const save = useCallback(
    (offset: number) => {
      if (!restoredRef.current) return;
      savedOffset.current = offset;
    },
    [savedOffset],
  );

  const finish = useCallback(() => {
    restoredRef.current = true;
    setRestoring(false);
  }, []);

  // 콘텐츠 높이가 mount 중 placeholder→실측으로 요동하므로, "처음 target 에 닿는 순간"
  // 한 번 복원하고 잠그면 이후 수축 시 스크롤이 새 max 로 클램프돼 엉뚱한 위치(7378 등)에
  // 머문다. 그래서 요동이 멈출 때까지(디바운스) target 으로 재적용하고, 안정된 뒤 확정한다.
  const seqRef = useRef(0);
  const scheduleStableFinish = useCallback(() => {
    const mySeq = ++seqRef.current;
    setSafeTimeout(() => {
      if (restoredRef.current) return;
      if (mySeq !== seqRef.current) return; // 그 사이 새 content-size 변화 → 아직 요동 중
      scrollTo(targetRef.current, false);
      finish();
    }, STABLE_MS);
  }, [setSafeTimeout, scrollTo, finish]);

  const onContentSizeChange = useCallback(
    (_width: number, contentHeight: number) => {
      if (restoredRef.current) return;
      const target = targetRef.current;
      if (target <= 0) {
        finish();
        return;
      }
      if (contentHeight >= target + viewportHeight) {
        // 도달 가능 → target 으로 재적용(요동 추종). 확정은 안정된 뒤에만.
        scrollTo(target, false);
        // 도달한 뒤에만 안정-확정을 예약한다. 아직 target 에 못 닿았는데(아래 else) 예약하면,
        // 지연 mount 섹션이 다음 프레임 안에 안 자라는 gap(>STABLE_MS) 동안 콜백이 안 와서
        // 조기 발화 → 짧은 contentHeight 에서 finish → scrollTo(target) 이 clamp 돼 위쪽에 잠긴다.
        // (실측: 1645px 에서 finish, 이후 22695px 까지 자란 콘텐츠는 restored=true 라 전부 무시됨)
        scheduleStableFinish();
      } else {
        // 아직 짧음: 지연 mount 섹션(useMountWhenNearViewport)은 뷰포트 근처에서만 깨어나므로,
        // 현재 바닥까지 밀어(clamp) 다음 섹션을 mount 시킨다 → 콘텐츠가 자라며 content-size 재발생
        // → 이 콜백이 다시 돌아 target 에 닿을 때까지 cascade. (리스트는 restoring 동안 숨김이라 안 보임)
        // 여기선 확정하지 않는다. target 에 끝내 못 닿는 경우의 종료는 아래 maxWaitMs backstop 이 맡는다.
        scrollTo(contentHeight, false);
      }
    },
    [viewportHeight, scrollTo, finish, scheduleStableFinish],
  );

  // 콘텐츠가 예전보다 짧거나 로딩이 늦어 target 에 못 닿는 경우의 backstop.
  useEffect(() => {
    if (targetRef.current <= 0) return;
    setSafeTimeout(() => {
      if (restoredRef.current) return;
      scrollTo(targetRef.current, false);
      finish();
    }, maxWaitMs);
  }, [setSafeTimeout, scrollTo, finish, maxWaitMs]);

  return { save, onContentSizeChange, restoring };
};
